import { useMemo, useCallback } from 'react';
import TelemetryGraph from './TelemetryGraph';

function interpolateTime(data, dist) {
  // Return null if outside data range — do not clamp/pad
  if (dist < data[0].dist || dist > data[data.length - 1].dist) return null;
  for (let i = 1; i < data.length; i++) {
    if (data[i].dist >= dist) {
      const d0 = data[i - 1].dist;
      const d1 = data[i].dist;
      const t0 = data[i - 1].time;
      const t1 = data[i].time;
      const frac = (dist - d0) / (d1 - d0 || 1);
      return t0 + frac * (t1 - t0);
    }
  }
  return null;
}

export default function LapDelta({
  referenceLap,
  comparisonLap,
  width = 700,
  height = 180,
  samplingMode = 'fit',
  viewStyle = 'standard',
  crosshairPos,
  onCrosshairChange,
  zoomRange,
  onZoomChange,
  onDragRangeChange,
  interactive,
  segmentBands,
  xAxisMode = 'distance',
  user,
}) {
  const deltaTrace = useMemo(() => {
    if (!referenceLap?.data?.length || !comparisonLap?.data?.length) return null;

    // Use the shorter lap's distance as the comparison window
    const refMaxDist = referenceLap.data[referenceLap.data.length - 1].dist;
    const cmpMaxDist = comparisonLap.data[comparisonLap.data.length - 1].dist;
    const maxDist = Math.min(refMaxDist, cmpMaxDist);
    if (maxDist <= 0) return null;

    // Use the fewer of the two point counts
    const numSamples = Math.min(referenceLap.data.length, comparisonLap.data.length);

    const points = [];
    for (let i = 0; i < numSamples; i++) {
      const d = (i / (numSamples - 1)) * maxDist;
      const refTime = interpolateTime(referenceLap.data, d);
      const cmpTime = interpolateTime(comparisonLap.data, d);
      // Only include points where both laps have valid data
      if (refTime != null && cmpTime != null) {
        // In time mode, use reference lap time as x-axis; in distance mode, use distance
        const x = xAxisMode === 'time' ? refTime : d;
        points.push({ dist: x, value: cmpTime - refTime });
      }
    }
    if (points.length < 2) return null;

    return [{ points, color: '#2e86c1', label: null }];
  }, [referenceLap, comparisonLap, xAxisMode]);

  const yRange = useMemo(() => {
    if (!deltaTrace?.[0]?.points?.length) return { min: -1, max: 1 };
    const values = deltaTrace[0].points.map((p) => p.value);
    const absMax = Math.max(Math.abs(Math.min(...values)), Math.abs(Math.max(...values)), 0.5);
    return { min: -absMax, max: absMax };
  }, [deltaTrace]);

  const deltaYFormat = useCallback(
    (v) => (v >= 0 ? '+' : '') + v.toFixed(2) + 's',
    []
  );

  const deltaCrosshairFormat = useCallback(
    (v) => ({
      text: (v >= 0 ? '+' : '') + v.toFixed(3) + 's',
      color: v <= 0 ? '#27ae60' : '#e74c3c',
    }),
    []
  );

  if (!deltaTrace) return null;

  return (
    <TelemetryGraph
      precomputedTraces={deltaTrace}
      title={referenceLap.driverLabel && comparisonLap.driverLabel && referenceLap.driverLabel !== comparisonLap.driverLabel
        ? `LAP DELTA: ${referenceLap.driverLabel} L${referenceLap.lapNum} - ${comparisonLap.driverLabel} L${comparisonLap.lapNum}`
        : `LAP DELTA: L${referenceLap.lapNum} vs L${comparisonLap.lapNum}`}
      titleColor="#2e86c1"
      yRange={yRange}
      yFormat={deltaYFormat}
      fillZeroLine
      crosshairFormat={deltaCrosshairFormat}
      width={width}
      height={height}
      samplingMode={samplingMode}
      viewStyle={viewStyle}
      crosshairPos={crosshairPos}
      onCrosshairChange={onCrosshairChange}
      zoomRange={zoomRange}
      onZoomChange={onZoomChange}
      onDragRangeChange={onDragRangeChange}
      interactive={interactive}
      segmentBands={segmentBands}
      xAxisMode={xAxisMode}
      user={user}
    />
  );
}
